import { useTranslation } from 'react-i18next';
import { useContext, useState, useEffect } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import Moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkSlash, faLink } from '@fortawesome/free-solid-svg-icons';
import generatePDF, { Margin } from 'react-to-pdf';
import AuthContext from '../../../Context/AuthContext';
import './CompanyInfo.scss';
import ClientService from '../../../Services/ClientService';
import StatusService from '../../../Services/StatusService';
import UserService from '../../../Services/UserService';
import TokenService from '../../../Services/TokenService';
import NotificationService from '../../../Services/NotificationService';
import LabeledInfo from '../../../Components/LabeledInfo/LabeledInfo';
import TabNav from '../../../Components/TabNav/TabNav';
import Equity from './Modules/Equity';
import Liquidity from './Modules/Liquidity';
import Management from './Modules/Management';
import Regulatory from './Modules/Regulatory/Regulatory';
import Accounts from './Modules/Accounts';
import Reports from './Modules/Reports';
import Score from './Modules/Score/Score';
import XpektorSlider from '../../../Components/XpektorSlider/XpektorSlider';
import XpektorButton from '../../../Components/XpektorButton/XpektorButton';
import BankConnectionModal from '../Modals/BankConnectionModal';
import ClientAuthLinkModal from '../Modals/ClientAuthLinkModal';
import TriggerModal from '../Modals/TriggerModal';
import GraphModal from '../Modals/GraphModal';

const gT = (t, key) => t(`page-company-info${key}`);
const gsT = (t, key) => t(`shared${key}`);

const HT = Object.freeze({
  equity: 1000,
  liquidity: 1001,
  management: 1002,
});

export default function CompanyInfo() {
  const { t } = useTranslation('app');
  const { clientId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const [client, setClient] = useState([]);
  const [clientLoaded, setClientLoaded] = useState(false);
  const [bankGiro, setBankGiro] = useState(null);
  const [address, setAddress] = useState(null);

  const [authState] = useContext(AuthContext);

  const [equityStatus, setEquityStatus] = useState({});
  const [liquidityStatus, setLiquidityStatus] = useState({});
  const [managementStatus, setManagementStatus] = useState({});
  const [activeTab, setActiveTab] = useState('score');
  const [tabIndex, setTabIndex] = useState(0);

  const [equityReportHistory, setEquityReportHistory] = useState([]);
  const [liquidityReportHistory, setLiquidityReportHistory] = useState([]);
  const [managementReportHistory, setManagementReportHistory] = useState([]);
  const [selectedGraphData, setSelectedGraphData] = useState([]);
  const [showGraphModal, setShowGraphModal] = useState(false);

  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [selectedTrigger, setSelectedTrigger] = useState({});

  const [showConnectBankModal, setShowConnectBankModal] = useState(false);
  const [showClientAuthLinkModal, setShowClientAuthLinkModal] = useState(false);

  const [lastUpdated, setLastUpdated] = useState(
    gsT(t, '.labels.not-available')
  );
  const [isImpersonating, setIsImpersonating] = useState(false);

  const formatDateTime = (dateTime) => {
    return Moment.utc(dateTime).local().format('YYYY-MM-DD HH:mm');
  };

  const onSelectStatus = (companyStatus) => {
    if (companyStatus != null && Object.keys(companyStatus).length > 0) {
      setLastUpdated(formatDateTime(companyStatus.dateCreated));
      return;
    }
    setLastUpdated(gsT(t, '.labels.not-available'));
  };

  const showEquity = () => {
    history.replace({ pathname: `/app/companyinfo/${clientId}/equity` });
    onSelectStatus(equityStatus);
  };

  const showLiquidity = () => {
    history.replace({ pathname: `/app/companyinfo/${clientId}/liquidity` });
    onSelectStatus(liquidityStatus);
  };

  const showManagement = () => {
    history.replace({ pathname: `/app/companyinfo/${clientId}/management` });
    onSelectStatus(managementStatus);
  };

  const showRegulatory = () => {
    history.replace({ pathname: `/app/companyinfo/${clientId}/regulatory` });
    setLastUpdated(gsT(t, '.labels.not-available'));
  };

  const showAccounts = () => {
    history.replace({ pathname: `/app/companyinfo/${clientId}/accounts` });
    setLastUpdated(gsT(t, '.labels.not-available'));
  };

  const showReports = () => {
    history.replace({ pathname: `/app/companyinfo/${clientId}/reports` });
    setLastUpdated(gsT(t, '.labels.not-available'));
  };

  const showScore = () => {
    history.replace({ pathname: `/app/companyinfo/${clientId}/score` });
    setLastUpdated(gsT(t, '.labels.not-available'));
  };

  useEffect(() => {
    const isLocation = (path) => {
      return window.location.href.indexOf(path) !== -1;
    };

    if (isLocation('score')) {
      setActiveTab('score');
      setTabIndex(0);
    } else if (isLocation('regulatory')) {
      setActiveTab('regulatory');
      setTabIndex(1);
    } else if (isLocation('equity')) {
      setActiveTab('equity');
      setTabIndex(2);
    } else if (isLocation('liquidity')) {
      setActiveTab('liquidity');
      setTabIndex(3);
    } else if (isLocation('management')) {
      setActiveTab('management');
      setTabIndex(4);
    } else if (isLocation('accounts')) {
      setActiveTab('accounts');
      setTabIndex(5);
    } else if (isLocation('reports')) {
      setActiveTab('reports');
      setTabIndex(6);
    } else {
      setActiveTab('score');
      setTabIndex(0);
    }
  }, [location]);

  useEffect(() => {
    const getData = async () => {
      const clientResponse = await ClientService.getClient(clientId)
        .then((response) => {
          setClient(response.data);
          setClientLoaded(true);
          document.title = `${gsT(t, '.labels.client')} - ${
            response.data.name
          }`;
          return response.data;
        })
        .catch(() => {});

      await ClientService.getClientSettings(clientId)
        .then((response) => {
          setAddress(
            `${response.data.address ?? ''}, ${
              response.data.postalCode ?? ''
            }, ${response.data.city ?? ''}`
          );
          setBankGiro(response.data.bankGiro);
        })
        .catch(() => {
          setAddress(gsT(t, '.labels.not-available'));
          setBankGiro(gsT(t, '.labels.not-available'));
        });

      if (clientResponse.externalConnectionActive === true) {
        await StatusService.getEquityLatest(clientId)
          .then((response) => {
            setEquityStatus(response.data);
          })
          .catch(() => {
            setEquityStatus(null);
          });

        await StatusService.getLiquidityLatest(clientId)
          .then((response) => {
            setLiquidityStatus(response.data);
          })
          .catch(() => {
            setLiquidityStatus(null);
          });

        await StatusService.getManagementLatest(clientId)
          .then((response) => {
            setManagementStatus(response.data);
          })
          .catch(() => {
            setManagementStatus(null);
          });
      } else {
        setAddress(gsT(t, '.labels.not-available'));
        setBankGiro(gsT(t, '.labels.not-available'));
        setLastUpdated(gsT(t, '.labels.not-available'));
        setEquityStatus(null);
        setLiquidityStatus(null);
        setManagementStatus(null);
      }

      const impersonated = TokenService.getImpersonationToken();
      setIsImpersonating(impersonated !== null);
      if (!impersonated) {
        const user = UserService.getCurrentUser();

        await NotificationService.SimpleNotificationSetViewed(clientId, user.id)
          .then(() => {})
          .catch(() => {});
      }
    };

    if (clientId) getData();
  }, [clientId]);

  const createPdf = (pdfContent) => {
    return generatePDF(pdfContent, {
      filename: `${
        activeTab === 'equity'
          ? gT(t, '.pdf-file-name.equity')
          : activeTab === 'liquidity'
          ? gT(t, '.pdf-file-name.liquidity')
          : activeTab === 'management'
          ? gT(t, '.pdf-file-name.management')
          : activeTab === 'regulatory'
          ? gT(t, '.pdf-file-name.regulatory')
          : activeTab === 'score'
          ? gT(t, '.pdf-file-name.score')
          : ''
      }-${Moment.utc().local().format('YYYY-MM-DD')}-${client.name}.pdf`,
      page: {
        margin: Margin.SMALL,
      },
    });
  };

  const getEquityHistory = async () => {
    if (equityReportHistory.length !== 0) {
      setSelectedGraphData(equityReportHistory);
      return;
    }
    await StatusService.getEquityReportHistory(client.id)
      .then((response) => {
        setEquityReportHistory(response.data);
        setSelectedGraphData(response.data);
      })
      .catch(() => {});
  };

  const getLiquidityHistory = async () => {
    if (liquidityReportHistory.length !== 0) {
      setSelectedGraphData(liquidityReportHistory);
      return;
    }
    await StatusService.getLiquidityReportHistory(client.id)
      .then((response) => {
        setLiquidityReportHistory(response.data);
        setSelectedGraphData(response.data);
      })
      .catch(() => {});
  };

  const getManagementHistory = async () => {
    if (managementReportHistory.length !== 0) {
      setSelectedGraphData(managementReportHistory);
      return;
    }
    await StatusService.getManagementReportHistory(client.id)
      .then((response) => {
        setManagementReportHistory(response.data);
        setSelectedGraphData(response.data);
      })
      .catch(() => {});
  };

  const getHistory = async (type) => {
    switch (type) {
      case HT.equity:
        getEquityHistory().then(() => setShowGraphModal(true));
        break;
      case HT.liquidity:
        getLiquidityHistory().then(() => setShowGraphModal(true));
        break;
      case HT.management:
        getManagementHistory().then(() => setShowGraphModal(true));
        break;
      default:
        break;
    }
  };

  const openTriggerModal = (trigger) => {
    setSelectedTrigger(trigger);
    setShowTriggerModal(true);
  };

  const renderModules = () => {
    return (
      <XpektorSlider activeSlideIndex={tabIndex}>
        <XpektorSlider.Slide hidden={activeTab !== 'score'}>
          <Score
            client={client}
            onPdfClick={(pdfContent) => createPdf(pdfContent)}
            t={t}
          />
        </XpektorSlider.Slide>
        <XpektorSlider.Slide hidden={activeTab !== 'regulatory'}>
          <Regulatory
            client={client}
            onPdfClick={(pdfContent) => createPdf(pdfContent)}
            isImpersonating={isImpersonating}
            t={t}
          />
        </XpektorSlider.Slide>
        <XpektorSlider.Slide hidden={activeTab !== 'equity'}>
          <Equity
            equityStatus={equityStatus}
            onPdfClick={(pdfContent) => createPdf(pdfContent)}
            onHistoryClick={() => getHistory(HT.equity)}
            onTriggerClick={(trigger) => openTriggerModal(trigger)}
            t={t}
          />
        </XpektorSlider.Slide>
        <XpektorSlider.Slide hidden={activeTab !== 'liquidity'}>
          <Liquidity
            liquidityStatus={liquidityStatus}
            onPdfClick={(pdfContent) => createPdf(pdfContent)}
            onHistoryClick={() => getHistory(HT.liquidity)}
            onTriggerClick={(trigger) => openTriggerModal(trigger)}
            t={t}
          />
        </XpektorSlider.Slide>
        <XpektorSlider.Slide hidden={activeTab !== 'management'}>
          <Management
            managementStatus={managementStatus}
            onPdfClick={(pdfContent) => createPdf(pdfContent)}
            onHistoryClick={() => getHistory(HT.management)}
            onTriggerClick={(trigger) => openTriggerModal(trigger)}
            t={t}
          />
        </XpektorSlider.Slide>
        <XpektorSlider.Slide hidden={activeTab !== 'accounts'}>
          <Accounts client={client} t={t} />
        </XpektorSlider.Slide>
        <XpektorSlider.Slide hidden={activeTab !== 'reports'}>
          <Reports client={client} t={t} />
        </XpektorSlider.Slide>
      </XpektorSlider>
    );
  };

  return (
    <div className="company-info-wrapper">
      <div className="company-info-head">
        <div className="company-info-details">
          <div className="company-info-title-row">
            <p className="company-title">{client.name}</p>
          </div>
          <div className="company-info-sub-title-row">
            <LabeledInfo
              label={gT(t, '.labeled-info.orgnr')}
              info={client.organizationNumber}
              animatedY
              animDelay={0.25}
              divider
            />
            {address != null && (
              <LabeledInfo
                label={gT(t, '.labeled-info.address')}
                info={address}
                animatedY
                animDelay={0.5}
                divider
              />
            )}
            {bankGiro != null && (
              <LabeledInfo
                label={gT(t, '.labeled-info.bank-giro')}
                info={bankGiro}
                animatedY
                animDelay={0.75}
              />
            )}
          </div>
        </div>
        <div className="action-button-container">
          <div className="action-button-row">
            {clientLoaded === true && (
              <>
                <XpektorButton
                  content={gsT(t, '.labels.accounting')}
                  icon={client.externalConnectionActive ? faLink : faLinkSlash}
                  styleClass={
                    client.externalConnectionActive ? 'connected' : ''
                  }
                  disabled
                />
                <XpektorButton
                  content={gsT(t, '.labels.bank')}
                  icon={client.bankConnectionActive ? faLink : faLinkSlash}
                  styleClass={
                    client.bankConnectionActive
                      ? 'connected'
                      : client.hadBankConnection
                      ? 'expired'
                      : ''
                  }
                  onClick={() => {
                    setShowConnectBankModal(true);
                  }}
                  disabled={
                    !client.bankConnectionActive && !client.hadBankConnection
                  }
                />
                {!client.unconnected && (
                  <XpektorButton
                    content={gsT(t, '.labels.skatteverket')}
                    icon={client.skvConnected ? faLink : faLinkSlash}
                    styleClass={client.skvConnected ? 'connected' : ''}
                    disabled
                  />
                )}
              </>
            )}
          </div>
          <div className="sub-action-row">
            {authState.user?.role > 0 && (
              <XpektorButton
                icon={faLink}
                content={gT(t, '.activationlink')}
                onClick={() => setShowClientAuthLinkModal(true)}
              />
            )}
          </div>
        </div>
      </div>
      <div className="company-info-nav-container">
        <TabNav activeIndex={tabIndex}>
          <TabNav.Tab
            title={gT(t, '.tab-nav-title.score')}
            active={tabIndex === 0}
            onClick={() => showScore()}
          />
          <TabNav.Tab
            title={gT(t, '.tab-nav-title.regulatory')}
            active={tabIndex === 1}
            onClick={() => showRegulatory()}
          />
          <TabNav.Tab
            title={gT(t, '.tab-nav-title.equity')}
            active={tabIndex === 2}
            onClick={() => showEquity()}
          />
          <TabNav.Tab
            title={gT(t, '.tab-nav-title.liquidity')}
            active={tabIndex === 3}
            onClick={() => showLiquidity()}
          />
          <TabNav.Tab
            title={gT(t, '.tab-nav-title.management')}
            active={tabIndex === 4}
            onClick={() => showManagement()}
          />
          <TabNav.Tab
            title={gT(t, '.tab-nav-title.account-analytics')}
            active={tabIndex === 5}
            onClick={() => showAccounts()}
          />
          <TabNav.Tab
            title={gT(t, '.tab-nav-title.reports')}
            active={tabIndex === 6}
            onClick={() => showReports()}
          />
        </TabNav>
      </div>
      <div className="sub-nav-container">
        {activeTab === 'equity' ||
        activeTab === 'liquidity' ||
        activeTab === 'management' ? (
          clientLoaded === true && !client.externalConnectionActive ? (
            <div className="status">
              <FontAwesomeIcon
                className="status-icon-broken error"
                icon={faLinkSlash}
              />
              <p className="status-text error">
                {gT(t, '.status-text-error.external-connection')}
              </p>
            </div>
          ) : (
            ''
          )
        ) : activeTab === 'accounts' && client.hadBankConnection ? (
          <div className="status">
            <FontAwesomeIcon
              className="status-icon-broken error"
              icon={faLinkSlash}
            />
            <p className="status-text error">
              {gT(t, '.status-text-error.bank-connection')}
            </p>
          </div>
        ) : (
          ''
        )}
        {lastUpdated !== gsT(t, '.labels.not-available') && (
          <LabeledInfo
            label={gT(t, '.labeled-info.values-updated')}
            info={lastUpdated}
            animatedY
            animDelay={0.25}
          />
        )}
      </div>
      {renderModules()}
      {showConnectBankModal && (
        <BankConnectionModal
          handleClose={() => setShowConnectBankModal(false)}
          client={client}
          t={t}
        />
      )}
      {showClientAuthLinkModal && (
        <ClientAuthLinkModal
          client={client}
          handleClose={() => setShowClientAuthLinkModal(false)}
          t={t}
        />
      )}
      {showGraphModal && (
        <GraphModal
          data={selectedGraphData}
          reportType={activeTab}
          handleClose={() => setShowGraphModal(false)}
          t={t}
        />
      )}
      {showTriggerModal && (
        <TriggerModal
          trigger={selectedTrigger}
          handleClose={() => {
            setShowTriggerModal(false);
            setSelectedTrigger({});
          }}
          t={t}
        />
      )}
    </div>
  );
}
